/**
 * Which quality preset should this machine start on?
 *
 * Until now every visitor got `ultra` — 4x2048 cascades, full-resolution GTAO,
 * SSR, TAA, motion blur and a 56-step volumetric march at native resolution —
 * regardless of whether they arrived on a workstation or a school Chromebook.
 * That is the single biggest reason the game is unplayable on ordinary
 * hardware, and it is not a rendering problem: the renderer never got told.
 *
 * This module answers the question once, at boot, from the GPU string. It is
 * only ever a STARTING POINT — `src/render/adaptive.js` measures the frame
 * times the machine actually produces and moves the internal resolution to
 * hold the target, so a wrong guess here costs a couple of seconds, not a
 * session. The guess exists so those first seconds are not a slideshow, and so
 * the permutation-baked settings the governor *cannot* change at runtime
 * (cascade count, volumetric step count) are sized correctly from the start.
 *
 * Deliberately conservative in the middle: an unrecognised GPU is far more
 * likely to be a laptop integrated part than a 4090, and `high` upgrades
 * itself within a few seconds on hardware that can take it, whereas `ultra`
 * on an UHD 620 spends those seconds at 6 fps.
 */

/** Renderer-string patterns, most specific first. */
const RULES = [
  // ---- software rasterisers: nothing will save these -----------------------
  [/swiftshader|llvmpipe|softpipe|software|microsoft basic|generic renderer/i, 'low'],

  // ---- phones and tablets --------------------------------------------------
  // Adreno 7xx / recent Mali are genuinely capable, but they are also thermally
  // limited and usually driving a high-DPI panel, which is the expensive part.
  [/adreno \(tm\) [78]\d\d|mali-g[78]\d/i, 'balanced'],
  [/adreno|mali|powervr|apple gpu/i, 'low'],

  // ---- Apple silicon -------------------------------------------------------
  [/apple m[3-9]/i, 'ultra'],
  [/apple m[12]\s*(pro|max|ultra)/i, 'ultra'],
  [/apple m[12]/i, 'high'],

  // ---- Intel ---------------------------------------------------------------
  [/intel.*arc/i, 'high'],
  [/iris xe|iris plus|iris pro|iris\(r\) xe/i, 'balanced'],
  [/uhd graphics (6[0-9]{2}|7[0-9]{2}|hd)/i, 'low'],
  [/intel.*(hd|uhd) graphics/i, 'low'],
  [/intel/i, 'balanced'],

  // ---- AMD -----------------------------------------------------------------
  [/radeon (rx )?(6[5-9]\d\d|7[5-9]\d\d|9\d\d\d)/i, 'ultra'],
  [/radeon (rx )?(5[5-9]\d\d|6[0-4]\d\d|7[0-4]\d\d)/i, 'high'],
  [/radeon.*(vega|graphics|integrated)/i, 'balanced'],
  [/radeon/i, 'high'],

  // ---- NVIDIA --------------------------------------------------------------
  // Laptop MX / T-series / low-end Quadro are entry parts that read as
  // "discrete" but perform like integrated ones. T500 is this dev machine.
  [/geforce mx|nvidia t[45]\d\d|quadro (p[0-6]\d\d|k\d)|nvs /i, 'balanced'],
  [/rtx [45]\d[5-9]\d|rtx [45]0[89]0/i, 'ultra'],
  [/rtx [23]0[6-9]\d|rtx [45]0[5-7]\d/i, 'ultra'],
  [/rtx|gtx 1[6-9]\d\d/i, 'high'],
  [/gtx (9[5-9]\d|10[5-8]\d)/i, 'high'],
  [/geforce|nvidia|quadro/i, 'high'],
];

/** Read the unmasked renderer string without disturbing the game's context. */
export function probeRenderer() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const gl =
      canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false }) ||
      canvas.getContext('webgl');
    if (!gl) return { renderer: '', webgl2: false, gl: null };
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '') : '';
    const webgl2 = typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext;
    // Hand the context back to the driver immediately; the game wants its own.
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return { renderer, webgl2 };
  } catch {
    return { renderer: '', webgl2: true };
  }
}

/**
 * The quality ladder, ascending in cost. Canonical: `core/config.js` re-exports
 * it and `ui/menu.js` builds its buttons from it, so the order lives in exactly
 * one place. It is declared HERE rather than in config.js because config.js
 * imports this module — the other direction would be a cycle.
 */
export const QUALITY_ORDER = ['low', 'balanced', 'medium', 'high', 'ultra'];

function demote(tier, steps) {
  const i = QUALITY_ORDER.indexOf(tier);
  return QUALITY_ORDER[Math.max(0, i - steps)] ?? tier;
}

/**
 * @returns {{ quality: string, renderer: string, reason: string }}
 */
export function detectTier() {
  const { renderer } = probeRenderer();

  let quality = 'high'; // unknown hardware: capable-but-not-heroic, then adapt
  let reason = 'unrecognised GPU — starting at high and letting the governor decide';

  for (const [re, tier] of RULES) {
    if (re.test(renderer)) {
      quality = tier;
      reason = `matched ${re.source.slice(0, 34)}`;
      break;
    }
  }

  // A weak CPU cannot feed this scene either: the world walk, the physics steps
  // and the AI brains are all main-thread. Two cores is a netbook.
  const cores = navigator.hardwareConcurrency || 4;
  if (cores <= 2) {
    quality = demote(quality, 2);
    reason += ` · ${cores} cores`;
  } else if (cores <= 4 && QUALITY_ORDER.indexOf(quality) > 2) {
    quality = demote(quality, 1);
    reason += ` · ${cores} cores`;
  }

  // `deviceMemory` is coarse (Chrome caps it at 8) but a 4 GB machine is
  // genuinely at risk from the procedural texture bake, not just from fill.
  const mem = navigator.deviceMemory;
  if (mem && mem <= 4) {
    quality = demote(quality, 1);
    reason += ` · ${mem}GB`;
  }

  // A phone-sized viewport on a big DPR is a fill trap whatever the GPU is.
  if (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) {
    quality = demote(quality, 1);
    reason += ' · touch device';
  }

  return { quality, renderer: renderer || 'unknown', reason };
}
